import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card, Table, Empty, Tag, Button, Typography, Drawer, Modal,
  Space, Tooltip, Alert, App, Spin, DatePicker, Row, Col, Input,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import {
  ReloadOutlined, FileTextOutlined, FileOutlined, PictureOutlined,
  CopyOutlined, DownloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ForwardOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { useTheme } from '../../contexts/useTheme';
import radarL2IngestService, {
  type RadarL2Execution,
  type RadarL2ExecutionStatus,
  type RadarL2LogEntry,
  type RadarL2ExecutionFilter,
} from '../../api/radarL2IngestService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_CONFIG: Record<RadarL2ExecutionStatus, { color: string; label: string; icon: React.ReactNode }> = {
  succeeded: { color: 'success', label: 'Succeeded', icon: <CheckCircleOutlined /> },
  failed: { color: 'error', label: 'Failed', icon: <CloseCircleOutlined /> },
  skipped: { color: 'default', label: 'Skipped', icon: <ForwardOutlined /> },
};

const PAGE_SIZE = 50;

function getPngFileName(tileUri: string | null): string | null {
  if (!tileUri) return null;
  return tileUri.split('/').pop() || null;
}

function createTextColumnFilter<T>(
  placeholder: string,
  getText: (record: T) => string | null,
): Pick<ColumnsType<T>[number], 'filterDropdown' | 'onFilter'> {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0] as string}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm({ closeDropdown: true })}
          size="small"
          style={{ marginBottom: 8, display: 'block' }}
          allowClear
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm({ closeDropdown: true })}>
            Filter
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm({ closeDropdown: true });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) => {
      const text = getText(record as T);
      if (!text) return false;
      return text.toLowerCase().includes(String(value).toLowerCase());
    },
  };
}

function matchesSelectedEvent(
  execution: { season_year: number | null; circuit_id: string | null; start_date: string | null },
  event: { season_year: number; circuit_id: string; start_date: string },
): boolean {
  return execution.season_year === event.season_year
    && execution.circuit_id === event.circuit_id
    && (execution.start_date?.slice(0, 10) === event.start_date.slice(0, 10));
}

export const RadarL2IngestPage = () => {
  const { selectedEvent } = useEvent();
  const { theme } = useTheme();
  const { message } = App.useApp();

  const [executions, setExecutions] = useState<RadarL2Execution[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(
    [dayjs().startOf('day'), dayjs().endOf('day')]
  );

  // Log drawer
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logExecution, setLogExecution] = useState<RadarL2Execution | null>(null);
  const [logEntries, setLogEntries] = useState<RadarL2LogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // PNG modal
  const [pngModalOpen, setPngModalOpen] = useState(false);
  const [pngExecution, setPngExecution] = useState<RadarL2Execution | null>(null);
  const [pngUrl, setPngUrl] = useState<string | null>(null);
  const [pngFileName, setPngFileName] = useState<string | null>(null);
  const [pngLoading, setPngLoading] = useState(false);
  const [pngError, setPngError] = useState<string | null>(null);
  const pngUrlRef = useRef<string | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fetchExecutions = useCallback(async (currentPage: number = 1) => {
    setLoading(true);

    const filter: RadarL2ExecutionFilter = {
      executed_after: dateRange[0].startOf('day').toISOString(),
      executed_before: dateRange[1].endOf('day').toISOString(),
    };

    try {
      const response = await radarL2IngestService.getExecutions(
        filter,
        PAGE_SIZE,
        (currentPage - 1) * PAGE_SIZE,
      );

      if (response.success && response.data) {
        setExecutions((response.data as unknown as RadarL2Execution[]) || []);
        setTotal((response.raw as any)?.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch radar L2 executions:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (selectedEvent) {
      const today = dayjs();
      const start = dayjs(selectedEvent.start_date);
      const end = dayjs(selectedEvent.end_date);
      const isActive = today.isAfter(start.startOf('day')) && today.isBefore(end.endOf('day'));

      if (isActive) {
        setDateRange([today.startOf('day'), today.endOf('day')]);
      } else {
        setDateRange([start.startOf('day'), start.endOf('day')]);
      }
    } else {
      setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
    }
  }, [selectedEvent]);

  useEffect(() => {
    setPage(1);
    fetchExecutions(1);
  }, [fetchExecutions]);

  const fetchLogs = useCallback(async (execution: RadarL2Execution) => {
    setLogExecution(execution);
    setLogDrawerOpen(true);
    setLogLoading(true);
    setLogError(null);
    setLogEntries([]);

    try {
      const response = await radarL2IngestService.getExecutionLogs(execution.id);
      if (response.success && response.data) {
        setLogEntries((response.data as unknown as RadarL2LogEntry[]) || []);
      } else {
        setLogError(response.errorData?.message || 'Failed to fetch logs');
      }
    } catch (err) {
      setLogError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setLogLoading(false);
    }
  }, []);

  const fetchPng = useCallback(async (execution: RadarL2Execution) => {
    if (pngUrlRef.current) {
      URL.revokeObjectURL(pngUrlRef.current);
      pngUrlRef.current = null;
    }

    setPngExecution(execution);
    setPngModalOpen(true);
    setPngLoading(true);
    setPngError(null);
    setPngUrl(null);
    setPngFileName(getPngFileName(execution.tile_uri));

    try {
      const blob = await radarL2IngestService.getExecutionPng(execution.id);
      const url = URL.createObjectURL(blob);
      pngUrlRef.current = url;
      setPngUrl(url);
    } catch (err) {
      setPngError(err instanceof Error ? err.message : 'Failed to fetch PNG tile');
    } finally {
      setPngLoading(false);
    }
  }, []);

  const closePngModal = useCallback(() => {
    setPngModalOpen(false);
    if (pngUrlRef.current) {
      URL.revokeObjectURL(pngUrlRef.current);
      pngUrlRef.current = null;
    }
    setPngUrl(null);
  }, []);

  useEffect(() => {
    return () => {
      if (pngUrlRef.current) {
        URL.revokeObjectURL(pngUrlRef.current);
      }
    };
  }, []);

  const downloadH5 = useCallback(async (execution: RadarL2Execution) => {
    const fileName = execution.file_path.split('/').pop() || 'radar.h5';
    try {
      message.loading({ content: 'Downloading H5 file...', key: 'h5-download' });
      const blob = await radarL2IngestService.downloadExecutionH5(execution.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      message.success({ content: 'Download started', key: 'h5-download' });
    } catch (err) {
      message.error({
        content: err instanceof Error ? err.message : 'Failed to download H5 file',
        key: 'h5-download',
      });
    }
  }, [message]);

  const downloadPng = useCallback(async () => {
    if (!pngExecution || !pngFileName) return;

    if (pngUrl) {
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = pngFileName;
      a.click();
      return;
    }

    try {
      message.loading({ content: 'Downloading PNG...', key: 'png-download' });
      const blob = await radarL2IngestService.getExecutionPng(pngExecution.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pngFileName;
      a.click();
      URL.revokeObjectURL(url);
      message.success({ content: 'Download started', key: 'png-download' });
    } catch (err) {
      message.error({
        content: err instanceof Error ? err.message : 'Failed to download PNG',
        key: 'png-download',
      });
    }
  }, [pngExecution, pngFileName, pngUrl, message]);

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const tableData = useMemo(() => {
    if (!selectedEvent) return [];
    return executions.filter((execution) => matchesSelectedEvent(execution, selectedEvent));
  }, [executions, selectedEvent]);

  const columns: ColumnsType<RadarL2Execution> = useMemo(() => [
    {
      title: 'Time (UTC)',
      dataIndex: 'executed_at',
      key: 'executed_at',
      width: 170,
      render: (t: string) => dayjs(t).utc().format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Scan Time',
      dataIndex: 'scan_timestamp',
      key: 'scan_timestamp',
      width: 170,
      render: (t: string | null) => t
        ? dayjs(t).utc().format('YYYY-MM-DD HH:mm:ss')
        : <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Succeeded', value: 'succeeded' },
        { text: 'Failed', value: 'failed' },
        { text: 'Skipped', value: 'skipped' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: RadarL2ExecutionStatus) => {
        const cfg = STATUS_CONFIG[status];
        return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'PNG',
      dataIndex: 'tile_uri',
      key: 'tile_uri',
      width: 170,
      ellipsis: true,
      ...createTextColumnFilter<RadarL2Execution>(
        'Filter by PNG name',
        (record) => getPngFileName(record.tile_uri),
      ),
      render: (uri: string | null) => {
        const name = getPngFileName(uri);
        return name ? (
          <Tooltip title={uri}>
            <Text code style={{ fontSize: 11 }}>{name}</Text>
          </Tooltip>
        ) : <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 90,
      render: (ms: number) => <Text>{formatDuration(ms)}</Text>,
    },
    {
      title: 'H5',
      dataIndex: 'file_path',
      key: 'file_path',
      ellipsis: true,
      ...createTextColumnFilter<RadarL2Execution>(
        'Filter by H5 name',
        (record) => record.file_path.split('/').pop() || null,
      ),
      render: (path: string) => (
        <Tooltip title={path}>
          <Text code style={{ fontSize: 11 }}>{path.split('/').pop()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      ellipsis: true,
      ...createTextColumnFilter<RadarL2Execution>(
        'Filter by error text',
        (record) => record.error_message,
      ),
      render: (err: string | null) => err ? (
        <Tooltip title={err}>
          <Text type="danger" ellipsis style={{ maxWidth: 180 }}>
            <ExclamationCircleOutlined /> {err}
          </Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      fixed: 'right',
      render: (_: unknown, record: RadarL2Execution) => (
        <Space size={0}>
          {record.tile_uri && (
            <Tooltip title="View PNG">
              <Button
                type="link"
                size="small"
                icon={<PictureOutlined />}
                onClick={() => fetchPng(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Download H5">
            <Button
              type="link"
              size="small"
              icon={<FileOutlined />}
              onClick={() => downloadH5(record)}
            />
          </Tooltip>
          <Tooltip title="View Logs">
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => fetchLogs(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ], [downloadH5, fetchLogs, fetchPng]);

  const formatLogText = (entries: RadarL2LogEntry[]) =>
    entries
      .map(e => {
        const ts = e.timestamp ? new Date(e.timestamp).toISOString() : '';
        return `${ts}  ${e.severity.padEnd(7)}  ${e.message}`;
      })
      .join('\n');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0, gap: 0 }}>
      {/* Title Card */}
      <Card
        styles={{ body: { padding: '8px 12px' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Radar L2 Processing</span>
            {selectedEvent && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.65)',
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                padding: '2px 10px',
                borderRadius: '4px',
              }}>
                {selectedEvent.name}
              </span>
            )}
          </div>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={() => fetchExecutions(page)}
                size="small"
                type="text"
              />
            </Tooltip>
          </div>
        }
      >
        {!selectedEvent && (
          <Empty description="Select an event to view radar L2 executions" />
        )}
      </Card>

      {/* Table Card */}
      <Card
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}
        styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}
      >
        {/* Filters */}
        <Row gutter={16} align="middle" style={{ marginBottom: 12, flexShrink: 0 }}>
          <Col>
            <Space size="small">
              <Text type="secondary">Date:</Text>
              <RangePicker
                value={dateRange}
                onChange={(v) => {
                  if (v && v[0] && v[1]) {
                    setDateRange([v[0], v[1]]);
                  }
                }}
                allowClear={false}
                size="small"
                style={{ width: 240 }}
              />
            </Space>
          </Col>
        </Row>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <Table<RadarL2Execution>
            columns={columns}
            dataSource={tableData}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1000, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (t) => `${t} executions`,
              onChange: (p) => {
                setPage(p);
                fetchExecutions(p);
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    selectedEvent
                      ? 'No radar L2 executions found for this event'
                      : 'Select an event to view radar L2 executions'
                  }
                />
              ),
            }}
          />
        </div>
      </Card>

      {/* PNG Modal */}
      <Modal
        title={
          <Space>
            <PictureOutlined />
            <span>{pngFileName || 'PNG Tile'}</span>
            {pngExecution && (
              <Tag color={STATUS_CONFIG[pngExecution.status]?.color}>
                {STATUS_CONFIG[pngExecution.status]?.label}
              </Tag>
            )}
          </Space>
        }
        open={pngModalOpen}
        onCancel={closePngModal}
        width="80%"
        footer={[
          <Button key="close" onClick={closePngModal}>
            Close
          </Button>,
          pngUrl && pngFileName ? (
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadPng}
            >
              Download
            </Button>
          ) : null,
        ]}
        styles={{
          body: {
            maxHeight: '75vh',
            overflowY: 'auto',
            textAlign: 'center',
          },
        }}
      >
        {pngExecution && (
          <div style={{ marginBottom: 12, fontSize: 12, textAlign: 'left', color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
            <div>H5: <Text code style={{ fontSize: 11 }}>{pngExecution.file_path}</Text></div>
            {pngExecution.tile_uri && (
              <div>PNG: <Text code style={{ fontSize: 11 }}>{pngExecution.tile_uri}</Text></div>
            )}
          </div>
        )}

        {pngLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
              Loading PNG from storage...
            </div>
          </div>
        )}

        {pngError && (
          <Alert
            type="error"
            message="Failed to load PNG"
            description={pngError}
            showIcon
          />
        )}

        {!pngLoading && !pngError && pngUrl && (
          <img
            src={pngUrl}
            alt={pngFileName || 'Radar PNG tile'}
            style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
          />
        )}
      </Modal>

      {/* Logs Drawer */}
      <Drawer
        title={
          <Space>
            <FileTextOutlined />
            <span>Radar L2 Logs</span>
            {logExecution && (
              <Tag color={STATUS_CONFIG[logExecution.status]?.color}>
                {STATUS_CONFIG[logExecution.status]?.label}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        size="large"
        open={logDrawerOpen}
        onClose={() => setLogDrawerOpen(false)}
        extra={
          <Space size="small">
            {logEntries.length > 0 && (
              <>
                <Tooltip title="Copy to clipboard">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(formatLogText(logEntries));
                      message.success('Logs copied to clipboard');
                    }}
                  />
                </Tooltip>
                <Tooltip title="Save as file">
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const blob = new Blob([formatLogText(logEntries)], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `radar-l2-${logExecution?.cloud_event_id || logExecution?.id?.slice(0, 8)}-logs.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  />
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => logExecution && fetchLogs(logExecution)}
              loading={logLoading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {logExecution && (
          <div style={{ marginBottom: 12, fontSize: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
            <div>File: <Text code style={{ fontSize: 11 }}>{logExecution.file_path}</Text></div>
            <div>Event ID: <Text code style={{ fontSize: 11 }}>{logExecution.cloud_event_id}</Text></div>
            {logExecution.scan_timestamp && (
              <div>Scan: {dayjs(logExecution.scan_timestamp).utc().format('YYYY-MM-DD HH:mm:ss')} UTC</div>
            )}
            <div>
              Executed: {dayjs(logExecution.executed_at).utc().format('YYYY-MM-DD HH:mm:ss')} UTC
              {' '}({formatDuration(logExecution.duration_ms)})
            </div>
            {logExecution.timings && (
              <div>Timings: <Text code style={{ fontSize: 11 }}>{logExecution.timings}</Text></div>
            )}
          </div>
        )}

        {logLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>Fetching logs from Cloud Logging...</div>
          </div>
        )}

        {logError && (
          <Alert
            type="error"
            message="Failed to load logs"
            description={logError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!logLoading && !logError && logEntries.length === 0 && (
          <Empty description="No log entries found" />
        )}

        {!logLoading && logEntries.length > 0 && (
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
              padding: '12px',
              borderRadius: '6px',
              maxHeight: 'calc(100vh - 240px)',
              overflow: 'auto',
            }}
          >
            {logEntries.map((entry, idx) => {
              const ts = entry.timestamp
                ? new Date(entry.timestamp).toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    fractionalSecondDigits: 3,
                  } as Intl.DateTimeFormatOptions)
                : '';
              const severityColor =
                entry.severity === 'ERROR' ? '#f5222d' :
                entry.severity === 'WARNING' ? '#faad14' :
                entry.severity === 'INFO' ? '#1890ff' :
                theme === 'dark' ? '#d9d9d9' : '#595959';

              return (
                <div key={idx} style={{ marginBottom: '2px' }}>
                  <span style={{ color: '#8c8c8c', marginRight: '8px' }}>{ts}</span>
                  <span style={{ color: severityColor }}>{entry.message}</span>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};
